import { useEffect, useCallback } from 'react';
import { memberId } from '@trello/session-cookie';
import { useSharedState } from '@trello/shared-state';
import { ModelCache } from 'app/scripts/db/model-cache';
import type { Member } from 'app/scripts/models/member';
import { colorBlindState } from './useIsColorBlind';

/**
 * Keeps the shared colorBlindState in sync with user prefs in ModelCache.
 * Meant to be a singleton; currently _only_ used in useColorBlindDOMMutations.
 */
export const useColorBlindStateUpdater = () => {
  const [isColorBlind, setIsColorBlind] = useSharedState(colorBlindState);
  const prefsChangeEvent = 'change:prefs';

  const updateColorBlindState = useCallback(
    (model) => {
      const prefs = model.get('prefs');
      const colorBlind = !!(prefs && prefs.colorBlind);

      setIsColorBlind(colorBlind);
    },
    [setIsColorBlind],
  );

  useEffect(() => {
    let cancelled = false;
    let model: Member;

    // Wait for the member to be loaded, update based on current color-blind
    // pref and subscribe to changes
    ModelCache.waitFor('Member', memberId, (_, m) => {
      if (m && !cancelled) {
        model = m;
        updateColorBlindState(model);
        model.on(prefsChangeEvent, updateColorBlindState);
      }
    });

    return () => {
      cancelled = true;

      if (model) {
        model.off(prefsChangeEvent, updateColorBlindState);
      }
    };
  }, [updateColorBlindState]);

  return isColorBlind;
};
